import React, { FC, useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from '@wix/wix-i18n-config';
import { Box, Button, ButtonSize, Loader } from '@wix/design-system';
import { getWixLoginUrl } from '@utils/urlUtils';
import { UserDetails, templateCheckout } from '@common/types';
import { useCurrencyContext } from '@components/Contexts/CurrencyContext';
import { useErrorMonitor, useHttpClient } from '@wix/fe-essentials-standalone';
import { TEMPLATE_CHECKOUT_API_URL, USER_LOGIN_PARAM } from '@utils/constants';
import CustomCodeConsentModal from '../CustomCodeConsentModal';
import { ICustomCodeConsentModalProps } from '../CustomCodeConsentModal/CustomCodeConsentModal';
import { usePriceCalculation } from '@components/Hooks/usePriceCalculation';

interface PurchaseTemplateButtonProps {
  price: number;
  templateId: string;
  buttonSize: ButtonSize | undefined;
  logBiClick?: () => void;
  userDetails?: UserDetails;
  language: string;
  showShortFreeCta?: boolean;
  setPurchaseError: (isError: boolean) => void;
  consentModalProps: {
    onModalBiClick: ICustomCodeConsentModalProps['logBiClick'];
    hasConsentModal: boolean;
    isMobileView: boolean;
  };
  hasShortCTA?: boolean;
  privacyPolicyUrl?: string;
}

const PurchaseTemplateButton: FC<PurchaseTemplateButtonProps> = ({
  price,
  templateId,
  buttonSize,
  logBiClick,
  userDetails,
  setPurchaseError,
  consentModalProps,
  hasShortCTA,
  showShortFreeCta,
  privacyPolicyUrl,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const readyToPurchaseButtonRef = React.useRef<any>(null);
  const [buttonWidth, setButtonWidth] = useState<string>('fit-content');
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();
  const {
    currencyData: { currencyCode },
  } = useCurrencyContext();

  const { getLocalizedTotalPrice } = usePriceCalculation();

  const [submitInProgress, setSubmitInProgress] = useState(false);

  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const closeModal = () => setIsConsentModalOpen(false);

  const email = _.get(userDetails, 'email');

  useEffect(() => {
    const purchaseAfterLogin = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has(USER_LOGIN_PARAM) && email) {
        urlParams.delete(USER_LOGIN_PARAM);
        const currentUrl = `${
          window.location.pathname
        }?${urlParams.toString()}`;
        window.history.replaceState(null, '', currentUrl);
        await getTemplateCheckout();
      }
    };
    purchaseAfterLogin();
  }, [userDetails]);

  const redirectToCheckoutPage = async () => {
    if (email) {
      await getTemplateCheckout();
    } else {
      const currentUrl = new URL(window.location.href);
      const loginUrl = new URL(getWixLoginUrl(language));
      currentUrl.searchParams.set(USER_LOGIN_PARAM, 'true');
      loginUrl.searchParams.set('redirectTo', currentUrl.toString());
      window.location.assign(loginUrl.toString());
    }
  };

  const getTemplateCheckout = async () => {
    setButtonWidth(
      readyToPurchaseButtonRef.current?.button?.current?.wrappedComponentRef
        ?.innerComponentRef?.offsetWidth || 'fit-content',
    );
    setSubmitInProgress(true);
    closeModal();
    setPurchaseError(false);
    try {
      const {
        data: { checkoutURL },
      } = await httpClient.post<templateCheckout>(
        TEMPLATE_CHECKOUT_API_URL,
        {
          templateId,
          language,
          currencyCode,
        },
        {
          headers: {
            'x-wix-currency': currencyCode,
          },
        },
      );
      if (checkoutURL) {
        window.location.assign(checkoutURL);
      }
    } catch (err) {
      setPurchaseError(true);
      errorMonitor.captureException(err as Error);
      setSubmitInProgress(false);
    }
  };

  const getPurchaseButtonCtaText = useCallback(() => {
    if (price === 0) {
      return showShortFreeCta
        ? t('marketplace.templates-page.free-purchase-minimized-mobile')
        : t('marketplace.templates-page.free-purchase-cta');
    }
    return hasShortCTA
      ? getLocalizedTotalPrice(price!)
      : t('marketplace.templates-page.purchase-cta', {
          amount: getLocalizedTotalPrice(price!),
        });
  }, [price, showShortFreeCta, hasShortCTA]);

  return (
    <>
      {submitInProgress ? (
        <Box width={buttonWidth}>
          <Button size={buttonSize} fullWidth skin="dark">
            <Loader size="tiny" />
          </Button>
        </Box>
      ) : (
        <Button
          ref={readyToPurchaseButtonRef}
          dataHook="purchase-template-cta"
          skin="dark"
          size={buttonSize}
          onClick={() => {
            logBiClick?.();
            consentModalProps?.hasConsentModal
              ? setIsConsentModalOpen(true)
              : redirectToCheckoutPage();
          }}
        >
          {getPurchaseButtonCtaText()}
        </Button>
      )}
      {isConsentModalOpen && (
        <CustomCodeConsentModal
          isOpen={isConsentModalOpen}
          onClose={closeModal}
          callback={redirectToCheckoutPage}
          logBiClick={consentModalProps?.onModalBiClick}
          isMobileView={consentModalProps?.isMobileView}
          privacyPolicyUrl={privacyPolicyUrl}
        />
      )}
    </>
  );
};

export default PurchaseTemplateButton;
